import {
  collection, addDoc, query, orderBy, onSnapshot, limit, doc, updateDoc, setDoc, getDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import { Message, User, Attachment } from '../types';

const generateId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).substring(2, 15);
};

export const chatService = {
  subscribeToUsers: (callback: (users: User[]) => void) => {
    if (!db) return () => {};
    const q = query(collection(db, 'users'), orderBy('lastSeen', 'desc'));
    return onSnapshot(q, (snapshot) => {
      const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));
      callback(users);
    });
  },
  updateUserStatus: async (userId: string, status: 'online' | 'offline') => {
    if (!db) return;
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { status, lastSeen: Date.now() });
  },
  updateUserCustomStatus: async (userId: string, customStatus: string) => {
    if (!db) return;
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { customStatus });
  },
  subscribeToUser: (userId: string, callback: (user: User) => void) => {
    if (!db) return () => {};
    const userRef = doc(db, 'users', userId);
    return onSnapshot(userRef, (doc) => {
      if (doc.exists()) {
        callback({ id: doc.id, ...doc.data() } as User);
      }
    });
  },

  createUser: async (user: User) => {
    if (!db) return;
    await setDoc(doc(db, 'users', user.id), user, { merge: true });
  },
  subscribeToMessages: (callback: (messages: Message[]) => void) => {
    if (!db) return () => {};
    const q = query(collection(db, 'messages'), orderBy('timestamp', 'desc'), limit(100));
    return onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Message)).reverse();
      callback(messages);
    });
  },
  sendMessage: async (senderId: string, content: string, attachments: Attachment[] = []) => {
    if (!db) throw new Error("Database not initialized");
    const newMessage: Omit<Message, 'id'> = {
      senderId, content, attachments, timestamp: Date.now()
    };
    await addDoc(collection(db, 'messages'), newMessage);
  },
  uploadFile: async (file: File): Promise<Attachment> => {
    if (!storage) throw new Error("Storage not initialized");
    const fileId = generateId();
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const storageRef = ref(storage, `attachments/${fileId}_${safeName}`);
    await uploadBytes(storageRef, file, { contentType: file.type });
    const url = await getDownloadURL(storageRef);
    return {
      id: fileId, type: file.type.startsWith('image/') ? 'image' : 'file', url, name: file.name, size: file.size
    };
  },
  subscribeToNotepad: (callback: (content: string) => void) => {
    if (!db) return () => {};
    const notepadRef = doc(db, 'notepad', 'main');
    return onSnapshot(notepadRef, (doc) => {
      if (doc.exists()) {
        callback(doc.data().content);
      }
    });
  },
  updateNotepad: async (content: string) => {
    if (!db) return;
    const notepadRef = doc(db, 'notepad', 'main');
    await setDoc(notepadRef, { content });
  },
  initNotepad: async () => {
    if (!db) return;
    const notepadRef = doc(db, 'notepad', 'main');
    const notepadSnap = await getDoc(notepadRef);
    if (!notepadSnap.exists()) {
      await setDoc(notepadRef, { content: 'Welcome to the shared notepad!' });
    }
  }
};
